import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StatusApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { KeyRound, ExternalLink, Save } from "lucide-react";

export default function SettingsDialog({ open, onOpenChange, settings, onSaved, onBackup }) {
  const [key, setKey] = useState("");
  const [gkey, setGkey] = useState("");
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const doBackup = async () => {
    if (!onBackup) return;
    setBackingUp(true);
    try { await onBackup(); } finally { setBackingUp(false); }
  };

  const save = async () => {
    if (!key.trim() && !gkey.trim()) {
      toast.error("Inserisci almeno una chiave");
      return;
    }
    setSaving(true);
    try {
      if (key.trim()) await StatusApi.saveApiKey(key.trim());
      if (gkey.trim()) await StatusApi.saveGeminiKey(gkey.trim());
      toast.success("Impostazioni salvate");
      setKey("");
      setGkey("");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold tracking-tight flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Impostazioni Google Places
          </DialogTitle>
          <DialogDescription>
            Incolla la tua chiave API di Google Cloud (Places API New). Serve per raccogliere i nominativi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm rounded-md border border-border bg-muted/50 p-3 text-muted-foreground">
            Stato attuale:{" "}
            {settings?.google_api_key_set ? (
              <span className="text-emerald-600 font-semibold">Configurata ({settings.masked})</span>
            ) : (
              <span className="text-red-500 font-semibold">Non configurata</span>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="apikey">Chiave API</Label>
            <Input
              id="apikey"
              data-testid="settings-apikey-input"
              placeholder="AIzaSy..."
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="font-mono-plex"
            />
          </div>
          <a
            href="https://console.cloud.google.com/apis/library/places.googleapis.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Apri Google Cloud Console <ExternalLink className="h-3 w-3" />
          </a>

          <div className="border-t border-border pt-3 mt-1 space-y-1.5">
            <div className="text-sm rounded-md border border-border bg-muted/50 p-3 text-muted-foreground">
              Gemini (ricerca AI):{" "}
              {settings?.gemini_api_key_set ? (
                <span className="text-emerald-600 font-semibold">Configurata ({settings.gemini_masked})</span>
              ) : (
                <span className="text-red-500 font-semibold">Non configurata</span>
              )}
            </div>
            <Label htmlFor="gkey">Chiave Gemini (Google AI Studio)</Label>
            <Input
              id="gkey"
              data-testid="settings-gemini-input"
              placeholder="AIzaSy... / AQ...."
              value={gkey}
              onChange={(e) => setGkey(e.target.value)}
              className="font-mono-plex"
            />
            <p className="text-[11px] text-muted-foreground">
              Serve per "Cerca contatti AI". Il grounding Google Search richiede fatturazione attiva su Google AI (~35$/1000 ricerche).
            </p>
          </div>

          <div className="border-t border-border pt-3 mt-1 space-y-2">
            <Label>Backup dati</Label>
            <p className="text-[11px] text-muted-foreground">
              Crea un archivio completo dei dati e lo invia via email (richiede dominio verificato su Resend).
            </p>
            <Button variant="outline" onClick={doBackup} disabled={backingUp} data-testid="settings-backup-btn" className="w-full">
              <Save className="h-4 w-4 mr-1.5" /> {backingUp ? "Backup in corso..." : "Esegui backup ora"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settings-cancel-btn">
            Annulla
          </Button>
          <Button onClick={save} disabled={saving} data-testid="settings-save-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? "Salvataggio..." : "Salva chiave"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
